<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { DxButton } from 'devextreme-vue/button';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxTextBox } from 'devextreme-vue/text-box';
import { DxCheckBox } from 'devextreme-vue/check-box';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxNumberBox } from 'devextreme-vue/number-box';
import { DxTagBox } from 'devextreme-vue/tag-box';
import { showError, showSuccess } from '@botto/core';
import { botPoliciesApi, type BotPolicy } from '../../api';

type BotAction = 'flag' | 'challenge' | 'block';
type CleanAction = 'redirect' | 'challenge';

type SimpleDetector = { enabled: boolean; action: BotAction };

type PolicyConfig = {
  clean_traffic_action: CleanAction;
  detectors: {
    ua: SimpleDetector;
    bot_isp: SimpleDetector;
    ip2proxy: SimpleDetector & {
      types: string[];
      vpn_types: string[];
      vpn_action: BotAction;
      /** undefined/empty in UI → null in API */
      fraud_score_threshold: number | undefined;
    };
    rate_limit: SimpleDetector & {
      max_clicks: number;
      window_seconds: number;
      subnet_v4_prefix: number;
      subnet_v6_prefix: number;
      block_ttl_seconds: number;
    };
    client_hints: SimpleDetector;
    header_fingerprint: SimpleDetector;
    behavioral_cookie: SimpleDetector & {
      cookie_name: string;
      max_age: number;
    };
    headless_js: SimpleDetector;
  };
};

const ACTION_ITEMS: BotAction[] = ['flag', 'challenge', 'block'];
const CLEAN_ITEMS: CleanAction[] = ['redirect', 'challenge'];

const IP2PROXY_TYPE_ITEMS = ['DCH', 'SES', 'AIC', 'VPN', 'TOR', 'PUB', 'WEB', 'RES', 'CPN', 'EPN'];
const IP2PROXY_VPN_TYPE_ITEMS = ['VPN', 'TOR', 'PUB', 'WEB', 'RES', 'CPN', 'EPN'];

const DETECTOR_META: Array<{ key: keyof PolicyConfig['detectors']; title: string; hint?: string }> = [
  { key: 'ua', title: 'User-Agent', hint: 'Известные бот-UA / crawler' },
  { key: 'bot_isp', title: 'Bot ISP', hint: 'ASN / ISP из списка бот-провайдеров' },
  { key: 'ip2proxy', title: 'IP2Proxy', hint: 'Типы прокси / VPN / fraud score' },
  { key: 'rate_limit', title: 'Rate limit', hint: 'Лимит кликов с IP / подсети' },
  { key: 'client_hints', title: 'Client Hints', hint: 'Несогласованные Client Hints' },
  { key: 'header_fingerprint', title: 'Header fingerprint', hint: 'Аномалии HTTP-заголовков' },
  { key: 'behavioral_cookie', title: 'Behavioral cookie', hint: 'Cookie повторных бот-визитов' },
  { key: 'headless_js', title: 'Headless JS', hint: 'JS-проверка headless в challenge' },
];

function defaultConfig(): PolicyConfig {
  return {
    clean_traffic_action: 'redirect',
    detectors: {
      ua: { enabled: true, action: 'block' },
      bot_isp: { enabled: true, action: 'block' },
      ip2proxy: {
        enabled: true,
        action: 'block',
        types: ['DCH', 'SES', 'AIC'],
        vpn_types: ['VPN', 'TOR', 'PUB', 'WEB', 'RES', 'CPN', 'EPN'],
        vpn_action: 'flag',
        fraud_score_threshold: undefined,
      },
      rate_limit: {
        enabled: false,
        action: 'block',
        max_clicks: 30,
        window_seconds: 60,
        subnet_v4_prefix: 24,
        subnet_v6_prefix: 64,
        block_ttl_seconds: 600,
      },
      client_hints: { enabled: false, action: 'challenge' },
      header_fingerprint: { enabled: false, action: 'flag' },
      behavioral_cookie: {
        enabled: false,
        action: 'challenge',
        cookie_name: 'tr_bot',
        max_age: 86400,
      },
      headless_js: { enabled: true, action: 'block' },
    },
  };
}

function asBotAction(v: unknown, fallback: BotAction): BotAction {
  return v === 'flag' || v === 'challenge' || v === 'block' ? v : fallback;
}

function asCleanAction(v: unknown, fallback: CleanAction): CleanAction {
  return v === 'redirect' || v === 'challenge' ? v : fallback;
}

function asStringArray(v: unknown, fallback: string[]): string[] {
  if (!Array.isArray(v)) return [...fallback];
  return v.map(String).filter(Boolean);
}

function asNumber(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function asOptionalNumber(v: unknown): number | undefined {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeConfig(raw: unknown): PolicyConfig {
  const base = defaultConfig();
  if (!raw || typeof raw !== 'object') return base;
  const src = raw as Record<string, unknown>;
  base.clean_traffic_action = asCleanAction(src.clean_traffic_action, base.clean_traffic_action);
  const dets = (src.detectors && typeof src.detectors === 'object'
    ? (src.detectors as Record<string, unknown>)
    : {}) as Record<string, Record<string, unknown> | undefined>;

  for (const key of Object.keys(base.detectors) as Array<keyof PolicyConfig['detectors']>) {
    const patch = dets[key];
    if (!patch || typeof patch !== 'object') continue;
    const cur = base.detectors[key] as Record<string, unknown>;
    cur.enabled = Boolean(patch.enabled);
    cur.action = asBotAction(patch.action, cur.action as BotAction);

    if (key === 'ip2proxy') {
      cur.types = asStringArray(patch.types, cur.types as string[]);
      cur.vpn_types = asStringArray(patch.vpn_types, cur.vpn_types as string[]);
      cur.vpn_action = asBotAction(patch.vpn_action, cur.vpn_action as BotAction);
      cur.fraud_score_threshold = asOptionalNumber(patch.fraud_score_threshold);
    }
    if (key === 'rate_limit') {
      cur.max_clicks = asNumber(patch.max_clicks, cur.max_clicks as number);
      cur.window_seconds = asNumber(patch.window_seconds, cur.window_seconds as number);
      cur.subnet_v4_prefix = asNumber(patch.subnet_v4_prefix, cur.subnet_v4_prefix as number);
      cur.subnet_v6_prefix = asNumber(patch.subnet_v6_prefix, cur.subnet_v6_prefix as number);
      cur.block_ttl_seconds = asNumber(patch.block_ttl_seconds, cur.block_ttl_seconds as number);
    }
    if (key === 'behavioral_cookie') {
      cur.cookie_name = String(patch.cookie_name ?? cur.cookie_name ?? 'tr_bot');
      cur.max_age = asNumber(patch.max_age, cur.max_age as number);
    }
  }
  return base;
}

function assignConfig(target: PolicyConfig, source: PolicyConfig) {
  target.clean_traffic_action = source.clean_traffic_action;
  for (const key of Object.keys(source.detectors) as Array<keyof PolicyConfig['detectors']>) {
    Object.assign(target.detectors[key], source.detectors[key]);
  }
}

function toApiConfig(cfg: PolicyConfig): Record<string, unknown> {
  const cloned = JSON.parse(JSON.stringify(cfg)) as Record<string, unknown>;
  const dets = cloned.detectors as Record<string, Record<string, unknown>>;
  if (dets?.ip2proxy && dets.ip2proxy.fraud_score_threshold == null) {
    dets.ip2proxy.fraud_score_threshold = null;
  }
  return cloned;
}

const policies = ref<BotPolicy[]>([]);
const loading = ref(false);
const showModal = ref(false);
const editing = ref<BotPolicy | null>(null);
const saving = ref(false);
const deletingId = ref<number | null>(null);
const formError = ref('');

const form = reactive({
  name: '',
  description: '',
  is_system_default: false,
  config: defaultConfig(),
});

async function load() {
  loading.value = true;
  try {
    policies.value = await botPoliciesApi.listBotPolicies();
  } catch (e) {
    showError(e);
    policies.value = [];
  } finally {
    loading.value = false;
  }
}

function openCreate() {
  editing.value = null;
  form.name = '';
  form.description = '';
  form.is_system_default = false;
  assignConfig(form.config, defaultConfig());
  formError.value = '';
  showModal.value = true;
}

function openEdit(p: BotPolicy) {
  editing.value = p;
  form.name = p.name;
  form.description = p.description || '';
  form.is_system_default = false;
  assignConfig(form.config, normalizeConfig(p.config));
  formError.value = '';
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

async function save() {
  formError.value = '';
  if (!form.name.trim()) {
    formError.value = 'Имя обязательно';
    return;
  }
  const config = toApiConfig(form.config);
  saving.value = true;
  try {
    if (editing.value) {
      await botPoliciesApi.updateBotPolicy(editing.value.id, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        config,
      });
      showSuccess('Политика обновлена');
    } else {
      await botPoliciesApi.createBotPolicy({
        name: form.name.trim(),
        description: form.description.trim() || null,
        config,
        is_system_default: form.is_system_default,
      });
      showSuccess('Политика создана');
    }
    closeModal();
    await load();
  } catch (e) {
    formError.value = e instanceof Error ? e.message : 'Ошибка сохранения';
  } finally {
    saving.value = false;
  }
}

async function makeDefault(p: BotPolicy) {
  try {
    await botPoliciesApi.setDefaultBotPolicy(p.id);
    showSuccess(`${p.name} — системный default`);
    await load();
  } catch (e) {
    showError(e);
  }
}

async function remove(p: BotPolicy) {
  if (!confirm(`Удалить политику «${p.name}»?`)) return;
  deletingId.value = p.id;
  try {
    await botPoliciesApi.deleteBotPolicy(p.id);
    showSuccess('Удалено');
    await load();
  } catch (e) {
    showError(e);
  } finally {
    deletingId.value = null;
  }
}

onMounted(load);
</script>

<template>
  <div class="bot-policies">
    <div class="bot-policies__toolbar">
      <DxButton text="Обновить" styling-mode="outlined" :disabled="loading" @click="load" />
      <DxButton text="Новая политика" type="default" @click="openCreate" />
    </div>

    <div v-if="loading" class="muted">Загрузка…</div>
    <table v-else-if="policies.length" class="bot-policies__table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Имя</th>
          <th>Default</th>
          <th>Описание</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="p in policies" :key="p.id">
          <td>{{ p.id }}</td>
          <td>{{ p.name }}</td>
          <td>{{ p.is_system_default ? 'да' : '—' }}</td>
          <td class="muted">{{ p.description || '—' }}</td>
          <td class="bot-policies__actions">
            <DxButton text="Изменить" styling-mode="outlined" @click="openEdit(p)" />
            <DxButton
              v-if="!p.is_system_default"
              text="Сделать default"
              styling-mode="outlined"
              @click="makeDefault(p)"
            />
            <DxButton
              v-if="!p.is_system_default"
              text="Удалить"
              type="danger"
              styling-mode="outlined"
              :disabled="deletingId === p.id"
              @click="remove(p)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="muted">Политик пока нет.</div>

    <DxPopup
      v-model:visible="showModal"
      :title="editing ? 'Редактировать политику' : 'Создать политику'"
      :show-close-button="true"
      :drag-enabled="true"
      width="820"
      height="auto"
      :max-height="'92vh'"
      @hiding="closeModal"
    >
      <template #content>
        <div class="policy-form">
          <div v-if="formError" class="error-banner">{{ formError }}</div>

          <div class="field">
            <label>Имя</label>
            <DxTextBox v-model:value="form.name" width="100%" />
          </div>
          <div class="field">
            <label>Описание</label>
            <DxTextBox v-model:value="form.description" width="100%" />
          </div>
          <div v-if="!editing" class="field">
            <DxCheckBox v-model:value="form.is_system_default" text="Сделать системным default" />
          </div>

          <section class="section">
            <h3 class="section__title">Общее</h3>
            <div class="field field--inline">
              <label>Clean traffic action</label>
              <DxSelectBox
                v-model:value="form.config.clean_traffic_action"
                :items="CLEAN_ITEMS"
                width="220"
              />
              <p class="muted hint">Действие для «чистого» трафика после проверок.</p>
            </div>
          </section>

          <section class="section">
            <h3 class="section__title">Детекторы</h3>
            <div
              v-for="meta in DETECTOR_META"
              :key="meta.key"
              class="detector"
              :class="{ 'detector--off': !form.config.detectors[meta.key].enabled }"
            >
              <div class="detector__head">
                <div>
                  <div class="detector__title">{{ meta.title }}</div>
                  <div v-if="meta.hint" class="muted hint">{{ meta.hint }}</div>
                </div>
                <DxCheckBox
                  v-model:value="form.config.detectors[meta.key].enabled"
                  text="Включён"
                />
              </div>

              <div class="detector__row">
                <div class="field field--compact">
                  <label>Action</label>
                  <DxSelectBox
                    v-model:value="form.config.detectors[meta.key].action"
                    :items="ACTION_ITEMS"
                    :disabled="!form.config.detectors[meta.key].enabled"
                    width="160"
                  />
                </div>

                <template v-if="meta.key === 'ip2proxy'">
                  <div class="field field--compact field--grow">
                    <label>Proxy types</label>
                    <DxTagBox
                      v-model:value="form.config.detectors.ip2proxy.types"
                      :items="IP2PROXY_TYPE_ITEMS"
                      :show-selection-controls="true"
                      apply-value-mode="useButtons"
                      :disabled="!form.config.detectors.ip2proxy.enabled"
                      width="100%"
                    />
                  </div>
                  <div class="field field--compact field--grow">
                    <label>VPN types</label>
                    <DxTagBox
                      v-model:value="form.config.detectors.ip2proxy.vpn_types"
                      :items="IP2PROXY_VPN_TYPE_ITEMS"
                      :show-selection-controls="true"
                      apply-value-mode="useButtons"
                      :disabled="!form.config.detectors.ip2proxy.enabled"
                      width="100%"
                    />
                  </div>
                  <div class="field field--compact">
                    <label>VPN action</label>
                    <DxSelectBox
                      v-model:value="form.config.detectors.ip2proxy.vpn_action"
                      :items="ACTION_ITEMS"
                      :disabled="!form.config.detectors.ip2proxy.enabled"
                      width="160"
                    />
                  </div>
                  <div class="field field--compact">
                    <label>Fraud score ≥</label>
                    <DxNumberBox
                      v-model:value="form.config.detectors.ip2proxy.fraud_score_threshold"
                      :min="0"
                      :max="100"
                      :show-clear-button="true"
                      :disabled="!form.config.detectors.ip2proxy.enabled"
                      width="120"
                      placeholder="нет"
                    />
                  </div>
                </template>

                <template v-else-if="meta.key === 'rate_limit'">
                  <div class="field field--compact">
                    <label>Max clicks</label>
                    <DxNumberBox
                      v-model:value="form.config.detectors.rate_limit.max_clicks"
                      :min="1"
                      :disabled="!form.config.detectors.rate_limit.enabled"
                      width="110"
                    />
                  </div>
                  <div class="field field--compact">
                    <label>Window, sec</label>
                    <DxNumberBox
                      v-model:value="form.config.detectors.rate_limit.window_seconds"
                      :min="1"
                      :disabled="!form.config.detectors.rate_limit.enabled"
                      width="110"
                    />
                  </div>
                  <div class="field field--compact">
                    <label>IPv4 /prefix</label>
                    <DxNumberBox
                      v-model:value="form.config.detectors.rate_limit.subnet_v4_prefix"
                      :min="8"
                      :max="32"
                      :disabled="!form.config.detectors.rate_limit.enabled"
                      width="110"
                    />
                  </div>
                  <div class="field field--compact">
                    <label>IPv6 /prefix</label>
                    <DxNumberBox
                      v-model:value="form.config.detectors.rate_limit.subnet_v6_prefix"
                      :min="16"
                      :max="128"
                      :disabled="!form.config.detectors.rate_limit.enabled"
                      width="110"
                    />
                  </div>
                  <div class="field field--compact">
                    <label>Block TTL, sec</label>
                    <DxNumberBox
                      v-model:value="form.config.detectors.rate_limit.block_ttl_seconds"
                      :min="1"
                      :disabled="!form.config.detectors.rate_limit.enabled"
                      width="120"
                    />
                  </div>
                </template>

                <template v-else-if="meta.key === 'behavioral_cookie'">
                  <div class="field field--compact field--grow">
                    <label>Cookie name</label>
                    <DxTextBox
                      v-model:value="form.config.detectors.behavioral_cookie.cookie_name"
                      :disabled="!form.config.detectors.behavioral_cookie.enabled"
                      width="100%"
                    />
                  </div>
                  <div class="field field--compact">
                    <label>Max age, sec</label>
                    <DxNumberBox
                      v-model:value="form.config.detectors.behavioral_cookie.max_age"
                      :min="60"
                      :disabled="!form.config.detectors.behavioral_cookie.enabled"
                      width="140"
                    />
                  </div>
                </template>
              </div>
            </div>
          </section>
        </div>
      </template>
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Отмена', onClick: closeModal }"
      />
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{
          text: saving ? 'Сохранение…' : 'Сохранить',
          type: 'default',
          disabled: saving,
          onClick: save,
        }"
      />
    </DxPopup>
  </div>
</template>

<style scoped>
.bot-policies__toolbar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
}

.bot-policies__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
}

.bot-policies__table th,
.bot-policies__table td {
  text-align: left;
  padding: 0.55rem 0.45rem;
  border-bottom: 1px solid #e4e9f0;
  vertical-align: top;
}

.bot-policies__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: flex-end;
}

.policy-form {
  max-height: calc(92vh - 140px);
  overflow: auto;
  padding-right: 0.25rem;
}

.section {
  margin-top: 1rem;
}

.section__title {
  margin: 0 0 0.65rem;
  font-size: 0.95rem;
  font-weight: 600;
}

.field {
  margin-bottom: 0.75rem;
}

.field label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.85rem;
  color: #475569;
}

.field--compact {
  margin-bottom: 0;
  min-width: 110px;
}

.field--grow {
  flex: 1 1 220px;
  min-width: 200px;
}

.field--inline .hint {
  margin-top: 0.35rem;
}

.detector {
  border: 1px solid #e4e9f0;
  border-radius: 8px;
  padding: 0.75rem 0.85rem;
  margin-bottom: 0.65rem;
  background: #fff;
}

.detector--off {
  opacity: 0.72;
  background: #f8fafc;
}

.detector__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
}

.detector__title {
  font-weight: 600;
  font-size: 0.92rem;
}

.detector__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 0.85rem;
  align-items: flex-end;
}

.error-banner {
  background: #fef2f2;
  color: #b91c1c;
  padding: 0.55rem 0.75rem;
  border-radius: 6px;
  margin-bottom: 0.75rem;
}

.muted {
  color: #64748b;
}

.hint {
  margin: 0.15rem 0 0;
  font-size: 0.8rem;
}
</style>
